import type { User } from "firebase/auth";
import { type RefObject, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { accountName, dotTitle, levelDot, sideWords } from "./fill";
import { CLUSTER_COLOUR, type Cluster, clusterPins, clusterTitle, type FirstView, type FitDone, firstFitStep, formatBox, IDLE_MS, pinColour, statusLabel } from "./mapView";
import { type GoogleMap, type Maps, type Marker, pointOf } from "./maps";
import { refreshFailure } from "./RefreshFailed";
import { type KeptPins, pinsKey } from "./store/pageViews";
import { useView } from "./store/useView";

export type PinLoad = api.PinList | "server" | "network" | "session" | "hidden" | undefined;

export const ONE_PIN_ZOOM = 15;
const MAX_ZOOM = 20;
const START_ZOOM = 4;
const FIT_PADDING = 48;
const DARK = "#0f1216";
export const PIN_AT_DOOR = "At the front door: the container pin is not set";
export const CHECK_PIN = "Rough geocode: check the pin";

export function pinFailure(error: unknown): Exclude<PinLoad, api.PinList | undefined> {
  const code = error instanceof api.ApiError ? error.status : 0;
  return code === 404 ? "hidden" : code === 401 || code === 403 ? "session" : code >= 500 ? "server" : "network";
}

/** Rule U-36: the account pins with no filter are a kept view; the leads and a filtered list are not. */
export const keepsPins = (kind: api.PinKind, filterKey: string) => kind === "accounts" && filterKey === "{}";

/** True when the kept pins are of another box than the map shows, and no fetch asked for this box yet. */
export const refetchBox = (keptBox: string | null | undefined, box: string | null, asked: string | null | undefined) =>
  keptBox !== undefined && keptBox !== box && asked !== box;

export interface PinLayerOptions {
  kind: api.PinKind;
  layer: api.PinLayer;
  filters: Record<string, string>;
  /** A new value fetches the pins again. */
  reload?: number;
  /** False holds the fetch, for example until the map loads. */
  enabled?: boolean;
  /** The ids that the page shows with its own markers. */
  skip?: string[];
  zIndex?: number;
  /** The marker gets the focus back when the box closes. */
  onPin: (pin: api.Pin, marker: Marker) => void;
  /** The points that give the first view when there are no pins. */
  fallback?: api.LatLng[];
  /** Gets the dot of each account pin, for the hover card of its cone. */
  watch?: (dot: HTMLElement, accountId: string, label: string, extra?: { words?: string }) => void;
}

/** The pins of a list on a map (F-MAP-1): fetch by the box of the view, cluster by zoom, fit the first view. */
export function usePinLayer(maps: Maps | null, map: RefObject<GoogleMap | null>, user: User, options: PinLayerOptions) {
  const { kind, layer, filters, reload = 0, enabled = true, skip = [], zIndex, fallback = [] } = options;
  const markers = useRef<Marker[]>([]);
  const fitDone = useRef<FitDone>("none");
  const fitting = useRef(false);
  const moved = useRef(false);
  const pick = useRef(options.onPin);
  pick.current = options.onPin;
  const watch = useRef(options.watch);
  watch.current = options.watch;
  const [box, setBox] = useState<string | null>(null);
  const [zoom, setZoom] = useState(START_ZOOM);
  const [fetched, setFetched] = useState<PinLoad>(undefined);
  const [fetching, setFetching] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const filterKey = JSON.stringify(filters);
  const skipKey = JSON.stringify([...skip].sort());
  const fallbackKey = JSON.stringify(fallback);
  const kept = keepsPins(kind, filterKey);
  const boxNow = useRef(box);
  boxNow.current = box;
  const asked = useRef<string | null | undefined>(undefined);
  const view = useView<KeptPins>(kept && enabled ? pinsKey(layer) : null, async (signal) => {
    const want = boxNow.current;
    asked.current = want;
    const started = performance.now();
    const list = await api.mapPins(await user.getIdToken(), kind, layer, filters, want, signal);
    console.info(`${kind} pins: ${list.pins.length} of ${list.total} in ${Math.round(performance.now() - started)} ms (kept view, ${want ? "box" : "all"})`);
    return { box: want, list };
  });
  const found: PinLoad = kept ? (view.data?.list ?? (view.error && !view.checking ? pinFailure(view.error) : undefined)) : fetched;
  const loading = kept ? view.checking || (view.data === undefined && !view.error) : fetching;
  const failed = kept && view.data && view.error && !view.checking ? refreshFailure(view.error) : null;
  const list = typeof found === "object" ? found : null;
  const shownFilter = useRef(filterKey);

  // The pin total counts only the box. Thus new filters fit the view to their pins again.
  useEffect(() => {
    if (shownFilter.current === filterKey) return;
    shownFilter.current = filterKey;
    fitDone.current = "none";
    moved.current = false;
    console.info(`${kind} map: the filters changed, so the view fits the matched pins again`);
    setBox(null);
  }, [filterKey]);

  useEffect(() => {
    const view = map.current;
    if (!maps || !view) return;
    let timer = 0;
    const idle = view.addListener("idle", () => {
      fitting.current = false;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        setZoom(view.getZoom() ?? START_ZOOM);
        if (fitDone.current === "final") setBox(boxOf(view));
      }, IDLE_MS);
    });
    const drag = view.addListener("dragstart", () => {
      moved.current = true;
    });
    const zoomed = view.addListener("zoom_changed", () => {
      if (fitDone.current !== "none" && !fitting.current) moved.current = true;
    });
    return () => {
      window.clearTimeout(timer);
      for (const listener of [idle, drag, zoomed]) listener.remove();
    };
  }, [maps]);

  useEffect(() => {
    if (!enabled || kept) return;
    let live = true;
    const started = performance.now();
    setFetching(true);
    user
      .getIdToken()
      .then((token) => api.mapPins(token, kind, layer, filters, box))
      .then((next) => {
        if (!live) return;
        setFetched(next);
        console.info(`${kind} pins: ${next.pins.length} of ${next.total} in ${Math.round(performance.now() - started)} ms`);
      })
      .catch((error) => {
        if (!live) return;
        const failed = pinFailure(error);
        setFetched(failed);
        console.warn(`${kind} pins: the pins did not load (${failed})`);
      })
      .finally(() => live && setFetching(false));
    return () => {
      live = false;
    };
  }, [enabled, kept, user, kind, layer, filterKey, box, attempt, reload]);

  // The kept pins are of one box: a new box, or a reload after an own change, fetches again when no fetch runs.
  const again = useRef(false);
  const shownReload = useRef(reload);
  if (shownReload.current !== reload) {
    shownReload.current = reload;
    again.current = true;
  }
  useEffect(() => {
    if (!kept || !enabled || view.checking) return;
    if (again.current || refetchBox(view.data?.box, box, asked.current)) {
      again.current = false;
      asked.current = box;
      view.reload();
    }
  }, [kept, enabled, view.checking, view.data?.box, box, reload]);

  // The points of today fit the map at once and the pins fit it when they load; after that the map keeps the view of the user.
  useEffect(() => {
    const view = map.current;
    if (!maps || !view) return;
    const step = firstFitStep(fitDone.current, found !== undefined, moved.current, list?.pins ?? [], fallback);
    if (step.action === "wait") return;
    if (step.action === "keep") {
      fitDone.current = "final";
      console.info(`${kind} map: the first view stays as it is (moved: ${moved.current})`);
      setBox(boxOf(view));
      return;
    }
    fitDone.current = step.done;
    console.info(`${kind} map: the first view fits ${step.view.points.length} ${step.view.source} points and leaves out ${step.view.dropped}${step.done === "final" ? "" : ", until the pins load"}`);
    fitting.current = true;
    fitTo(maps, view, step.view);
  }, [maps, found, fallbackKey]);

  useEffect(() => {
    const view = map.current;
    if (!maps || !view) return;
    for (const marker of markers.current) marker.map = null;
    const made: Marker[] = [];
    const hidden = new Set(skip);
    const add = (options: Record<string, unknown>, click: (marker: Marker) => void) => {
      const marker = new maps.marker.AdvancedMarkerElement({ map: view, gmpClickable: true, ...(zIndex === undefined ? {} : { zIndex }), ...options });
      marker.addListener("click", () => click(marker));
      made.push(marker);
    };
    const free = (list?.pins ?? []).filter((pin) => !hidden.has(pin.id));
    for (const cluster of clusterPins(free, zoom)) {
      if (cluster.items.length === 1) {
        const pin = cluster.items[0];
        const position = { lat: pin.lat, lng: pin.lng };
        if (kind === "accounts") {
          const title = dotTitle([accountName(pin.number, pin.name), statusLabel(kind, pin.status), pin.fallback && PIN_AT_DOOR, pin.check && CHECK_PIN], pin.fill, pin.float);
          const content = levelDot({ share: pin.fill ?? null, check: pin.check });
          watch.current?.(content, pin.id, accountName(pin.number, pin.name), { words: pin.float ? sideWords(pin.float, pin.fill) : undefined });
          add({ position, title, content }, (marker) => pick.current(pin, marker));
          continue;
        }
        const door = pin.check ? { glyph: "?" } : {};
        const glyph = new maps.marker.PinElement({ ...door, background: pinColour(kind, pin.status), borderColor: DARK, glyphColor: DARK, scale: 0.85 });
        add({ position, title: `${pin.name} · ${statusLabel(kind, pin.status)}`, content: glyph.element }, (marker) => pick.current(pin, marker));
      } else {
        add({ position: { lat: cluster.lat, lng: cluster.lng }, title: clusterTitle(cluster.items.length, kind), content: clusterElement(cluster) }, () => {
          view.setCenter({ lat: cluster.lat, lng: cluster.lng });
          view.setZoom(Math.min((view.getZoom() ?? zoom) + 2, MAX_ZOOM));
        });
      }
    }
    markers.current = made;
  }, [maps, list, zoom, skipKey, kind, zIndex]);

  useEffect(
    () => () => {
      for (const marker of markers.current) marker.map = null;
    },
    [],
  );

  return { found, list, loading, failed, retry: () => (kept ? view.reload() : setAttempt((n) => n + 1)) };
}

function boxOf(view: GoogleMap): string | null {
  const bounds = view.getBounds();
  if (!bounds) return null;
  const south = pointOf(bounds.getSouthWest());
  const north = pointOf(bounds.getNorthEast());
  return formatBox({ south: south.lat, west: south.lng, north: north.lat, east: north.lng });
}

function fitTo(maps: Maps, view: GoogleMap, first: FirstView) {
  if (first.single) {
    view.setCenter(first.points[0]);
    view.setZoom(ONE_PIN_ZOOM);
    return;
  }
  const bounds = new maps.maps.LatLngBounds();
  for (const point of first.points) bounds.extend(point);
  view.fitBounds(bounds, FIT_PADDING);
}

/** A rounded square with "+" after the count, so a cluster never looks like a numbered stop. */
function clusterElement(cluster: Cluster<api.Pin>): HTMLElement {
  const element = document.createElement("div");
  element.className = "map-cluster";
  element.style.background = CLUSTER_COLOUR;
  element.textContent = `${cluster.items.length > 999 ? `${Math.floor(cluster.items.length / 1000)}k` : cluster.items.length}+`;
  return element;
}
